import React from "react";
import { Link } from "react-router-dom";
import {
  GraduationCap,
  MapPin,
  Phone,
  Mail,
  Clock,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  ChevronRight,
} from "lucide-react";
import { collegeInfo } from "../mock/mock";

const Footer = () => {
  const quickLinks = [
    { label: "About Us", to: "/about" },
    { label: "Courses", to: "/courses" },
    { label: "Facilities", to: "/facilities" },
    { label: "Faculty", to: "/faculty" },
    { label: "Gallery", to: "/gallery" },
    { label: "Contact", to: "/contact" },
  ];

  return (
    <footer className="bg-brand-green-deep text-brand-ivory relative overflow-hidden">
      {/* Top ornament */}
      <div className="h-1 w-full bg-gradient-to-r from-transparent via-brand-gold to-transparent" />

      <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        {/* College info */}
        <div className="lg:col-span-1">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-brand-green flex items-center justify-center border-2 border-brand-gold">
              <GraduationCap className="w-6 h-6 text-brand-gold" />
            </div>
            <div>
              <div className="font-serif-display text-lg font-bold">SHES D.Ed College</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-brand-gold">Shre Hingulmabika Edu. Society</div>
            </div>
          </div>
          <p className="text-sm text-brand-ivory/75 leading-relaxed">
            Shaping the future of education through excellence in teacher training since {collegeInfo.established}.
          </p>
          <div className="flex items-center gap-3 mt-5">
            {[Facebook, Instagram, Twitter, Youtube].map((Icon, i) => (
              <a
                key={i}
                href="#"
                className="w-9 h-9 rounded-full border border-brand-gold/40 flex items-center justify-center hover:bg-brand-gold hover:text-brand-green-deep transition-colors"
                aria-label="social"
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="text-brand-gold font-serif-display text-lg mb-5">Quick Links</h4>
          <ul className="space-y-2.5">
            {quickLinks.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="text-sm text-brand-ivory/80 hover:text-brand-gold flex items-center gap-1.5 transition-colors group"
                >
                  <ChevronRight className="w-3.5 h-3.5 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all text-brand-gold" />
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h4 className="text-brand-gold font-serif-display text-lg mb-5">Reach Us</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <MapPin className="w-4 h-4 mt-0.5 text-brand-gold shrink-0" />
              <span className="text-brand-ivory/80">{collegeInfo.address}</span>
            </li>
            <li className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-brand-gold shrink-0" />
              <a href={`tel:${collegeInfo.phone}`} className="text-brand-ivory/80 hover:text-brand-gold transition-colors">
                {collegeInfo.phone}
              </a>
            </li>
            <li className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-brand-gold shrink-0" />
              <a href={`mailto:${collegeInfo.email}`} className="text-brand-ivory/80 hover:text-brand-gold transition-colors break-all">
                {collegeInfo.email}
              </a>
            </li>
            <li className="flex items-center gap-3">
              <Clock className="w-4 h-4 text-brand-gold shrink-0" />
              <span className="text-brand-ivory/80">{collegeInfo.hours}</span>
            </li>
          </ul>
        </div>

        {/* Accreditations */}
        <div>
          <h4 className="text-brand-gold font-serif-display text-lg mb-5">Accreditations</h4>
          <ul className="space-y-2.5 text-sm text-brand-ivory/80">
            {collegeInfo.affiliations.map((a) => (
              <li key={a} className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-gold mt-2 shrink-0" />
                {a}
              </li>
            ))}
          </ul>
          <Link
            to="/admissions"
            className="mt-6 inline-flex items-center gap-2 btn-gold px-5 py-2.5 rounded-full text-sm font-semibold"
          >
            Apply for Admission <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      <div className="border-t border-brand-gold/20">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-brand-ivory/60">
          <p>&copy; {new Date().getFullYear()} Shre Hingulmabika Educational Society. All rights reserved.</p>
          <p>Designed with care for the future of Indian education.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
